import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDocContent } from '../../hooks/useDocs';
import { parseMarkdown, resolveHtmlAssetPaths, attachCodeBlockCopy } from '../../utils/markdown';
import Tag from '../ui/Tag';
import Button from '../ui/Button';
import DocToolsBar from './DocToolsBar';
import styles from './DocReader.module.css';

export default function DocReader() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();

  // Build the full slug: "category/doc-slug"
  const docSlug = slug ? `${category}/${slug}` : (category ?? '');

  const { content: rawContent, loading, error } = useDocContent(docSlug);
  const [parsed, setParsed] = useState<{ data: Record<string, unknown>; content: string } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rawContent) {
      setParsed(null);
      return;
    }
    const result = parseMarkdown(rawContent);
    // Resolve relative asset paths to /api/files/ URLs
    result.content = resolveHtmlAssetPaths(result.content, docSlug);
    setParsed(result);
  }, [rawContent, docSlug]);

  // Attach copy-button handlers to rendered code blocks
  useEffect(() => {
    if (!contentRef.current) return;
    return attachCodeBlockCopy(contentRef.current);
  }, [parsed]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingBar} />
        <span>LOADING ARCHIVE...</span>
      </div>
    );
  }

  if (error || !parsed) {
    return (
      <div className={styles.notFound}>
        <h2>ARCHIVE NOT FOUND</h2>
        <p>{error || 'The requested document does not exist.'}</p>
        <Button onClick={() => navigate(-1)}>GO BACK</Button>
      </div>
    );
  }

  const { data } = parsed;
  // Convert /raw/ paths in attachments to /api/files/
  const rawAttachments = (data.attachments as Array<{ name: string; file: string; desc: string; url: string }> | undefined) || [];
  const attachments = rawAttachments.map((a) => ({
    ...a,
    url: a.url ? a.url.replace(/^\/raw\//, '/api/files/') : a.url,
  }));

  return (
    <article className={styles.reader}>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className={styles.back}>
        ← BACK
      </Button>

      <header className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.metaLeft}>
            <span className={styles.date}>
              {data.date ? new Date(data.date as string).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }) : ''}
            </span>
            <span className={styles.category}>[{category?.toUpperCase()}]</span>
          </div>
          <DocToolsBar attachments={attachments} docSlug={docSlug} />
        </div>

        <h1 className={styles.title}>{data.title as string}</h1>
        <div className={styles.tags}>
          {(data.tags as string[] || []).map((tag: string) => (
            <Tag key={tag} variant="cyan" size="sm">{tag}</Tag>
          ))}
        </div>
      </header>

      <div
        ref={contentRef}
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: parsed.content }}
      />
    </article>
  );
}
